import Promise from 'bluebird'

import * as types from '../mutation-types'
import api from '@/api'

const defaultState = {
  username: '',
  isSuperuser: false,
  isLoggedIn: false,
  isAgencyUser: false,
  isGroupUser: false,
  isShopUser: false,
  shopData: {
    name: null,
    hasCrmLicense: false,
    hasSalesReportLicense: false,
    hasReserveLicense: false,
  },
  groupData: {
    name: null,
    hasCrmLicense: false,
    hasSalesReportLicense: false,
    hasReserveLicense: false,
  },
  crmServer: {},
  asShop: null,
  asGroup: null,
}

const state = {...defaultState}

const mutations = {
  [types.LOGGED_IN] (state, data) {
    state.isLoggedIn = true
    state.isAgencyUser = data.isAgencyUser
    state.isGroupUser = data.isGroupUser
    state.isShopUser = data.isShopUser
    if (data.shop) {
      Object.assign(state.shopData, data.shop)
      if (data.shop.crmServer) {
        state.crmServer = data.shop.crmServer
      }
    }

    if (data.group) {
      Object.assign(state.groupData, data.group)
      if (data.group.crmServer) {
        state.crmServer = data.group.crmServer
      }
    }

    if (state.crmServer && state.crmServer.apiEndpoint) {
      api.setCrmApiEndpoint(state.crmServer.apiEndpoint)
    }
  },
  [types.LOGGED_OUT] (state) {
    Object.assign(state, defaultState)
    api.reset()
    const licenseCode = localStorage.getItem('licenseCode')
    localStorage.clear()
    if (licenseCode) {
      localStorage.setItem('licenseCode', licenseCode)
    }
  },
  SET_AS_SHOP (state, data) {
    state.shopData = data
    state.hasCrmLicense = data.hasCrmLicense
    state.hasSalesReportLicense = data.hasSalesReportLicense
    state.hasReserveLicense = data.hasReserveLicense
    state.asShop = data.pk
    state.crmServer = data.crmServer
    api.setCrmApiEndpoint(state.crmServer.apiEndpoint)
    api.setAsShop(data.pk)
    localStorage.setItem('asShop', data.pk)
  },
  RESET_AS_SHOP (state) {
    state.asShop = null
    api.setAsShop(null)
    api.setCrmApiEndpoint(null)
    Object.assign(state.shopData, defaultState.shopData)
    if (state.groupData.crmServer && state.groupData.crmServer.apiEndpoint) {
      api.setCrmApiEndpoint(state.groupData.crmServer.apiEndpoint)
    }
    localStorage.removeItem('asShop')
  },
  SET_AS_GROUP (state, data) {
    state.groupData = data
    state.asGroup = data.pk
    state.crmServer = data.crmServer
    api.setCrmApiEndpoint(state.crmServer.apiEndpoint)
    api.setAsGroup(data.pk)
    localStorage.setItem('asGroup', data.pk)
  },
  RESET_AS_GROUP (state) {
    state.asGroup = null
    api.setAsGroup(null)
    api.setCrmApiEndpoint(null)
    Object.assign(state.groupData, defaultState.groupData)
    localStorage.removeItem('asGroup')
  },
}

const actions = {
  login ({commit}, [username, password, license, saveToken]) {
    return api.auth.login(username, password, license).then(res => {
      let token = res.body.token
      api.setToken(token)
      if (saveToken) {
        localStorage.setItem('token', token)
      }
      return api.accounts.profile().then(res => {
        commit(types.LOGGED_IN, res.body)
      })
    })
  },
  changeLogin ({commit}, token) {
    // 店舗もグループも処理としては同じ
    commit(types.LOGGED_OUT)
    api.setToken(token)
    // 画面更新ができなくなるので切り替えたtokenを一応保存
    localStorage.setItem('token', token)
    return api.accounts.profile().then(res => {
      commit(types.LOGGED_IN, res.body)
    })
  },
  tokenLogin ({commit}, redirectToken) {
    return api.auth.tokenLogin(redirectToken).then(res => {
      const token = res.body.token
      api.setToken(token)
      return api.accounts.profile().then(res => {
        commit(types.LOGGED_IN, res.body)
      })
    })
  },
  logout ({commit}) {
    commit(types.LOGGED_OUT)
    return Promise.resolve()
  },
  tryLoggedIn ({commit}) {
    let token = localStorage.getItem('token')
    if (!token) {
      return Promise.reject(new Error('token not found'))
    }
    return api.auth.verify(token).then(res => {
      api.setToken(token)
      return api.accounts.profile().then(res => {
        commit(types.LOGGED_IN, res.body)
      })
    })
  },
  trySetAsGroup ({commit}) {
    let groupPk = localStorage.getItem('asGroup')
    if (groupPk) {
      return api.groups.detail(groupPk).then(res => {
        commit('SET_AS_GROUP', res.body)
      })
    }
    return Promise.reject(new Error('no set as group'))
  },
  trySetAsShop ({commit}) {
    let shopPk = localStorage.getItem('asShop')
    if (shopPk) {
      return api.shops.detail(shopPk).then(res => {
        commit('SET_AS_SHOP', res.body)
      })
    }
    return Promise.reject(new Error('no set as shop'))
  },
  setAsShop ({commit}, shopPk) {
    return api.shops.detail(shopPk).then(res => {
      commit('SET_AS_SHOP', res.body)
    })
  },
  resetAsShop ({commit}) {
    commit('RESET_AS_SHOP')
  },
  setAsGroup ({commit}, groupPk) {
    return api.groups.detail(groupPk).then(res => {
      commit('SET_AS_GROUP', res.body)
    })
  },
  resetAsGroup ({commit}) {
    commit('RESET_AS_GROUP')
  },
}

const getters = {
  isLoggedIn: state => state.isLoggedIn,
  isAgencyUser: state => state.isAgencyUser,
  asShop: state => state.asShop,
  hasShop: state => {
    if (state.isShopUser || state.asShop) {
      return true
    }
    return false
  },
  hasGroup: state => {
    if (state.isGroupUser || state.asGroup) {
      return true
    }
    return false
  },
  hasShopOrGroup: (state, getters) => {
    return getters.hasShop || getters.hasGroup
  },
  shopName: state => {
    if (state.isShopUser || state.asShop) {
      return state.shopData.name
    } else if (state.isGroupUser || state.asGroup) {
      return state.groupData.name
    }
    return ''
  },
  groupName: state => state.groupData.name,
  hasCrmLicense: state => {
    if (state.isShopUser || state.asShop) {
      return state.shopData.hasCrmLicense
    } else if (state.isGroupUser || state.asGroup) {
      return state.groupData.hasCrmLicense
    }
    return false
  },
  hasSalesReportLicense: state => {
    if (state.isShopUser || state.asShop) {
      return state.shopData.hasSalesReportLicense
    } else if (state.isGroupUser || state.asGroup) {
      return state.groupData.hasSalesReportLicense
    }
    return false
  },
  hasReserveLicense: state => {
    if (state.isShopUser || state.asShop) {
      return state.shopData.hasSalesReportLicense
    } else if (state.isGroupUser || state.asGroup) {
      return state.groupData.hasReserveLicense
    }
    return false
  },
  isGroupMode: (state, getters) => {
    return getters.hasGroup && !getters.hasShop
  },
}

export default {
  state,
  actions,
  getters,
  mutations,
  namespaced: true,
}



// WEBPACK FOOTER //
// ./src/store/modules/account.js